import { deriveSessionStatus } from '../domain/training.ts';
import type { RoutineRevision, SessionStatus, TrainingResult } from '../domain/training.ts';
import type { TrainingRepository, PlannedSessionView, PlanSessionCommand, ReprogramSessionEffect } from '../ports/training-repository.ts';
import { intent } from './training-intent.ts';
export type { PlannedSessionView } from '../ports/training-repository.ts';
export interface PlanningView extends PlannedSessionView { routine: RoutineRevision; status: SessionStatus; executionId?: string }
export interface PlanningSaved { view: PlanningView; effect: ReprogramSessionEffect }
type Port = Pick<TrainingRepository, 'readPlannedSession' | 'readPlannedSessionByDate' | 'readPlannedSessionRevision' | 'planSession' | 'reprogramSession' | 'omitSession'>;
type ContextPort = Pick<TrainingRepository, 'readContext' | 'readRoutineRevision'>;
export class TrainingPlanning {
  constructor(private readonly repository: Port, private readonly catalog: ContextPort, private readonly id: () => string,
    private readonly executions?: Pick<TrainingRepository, 'readExecutionForPlannedSession'>) {}
  private async present(view: PlannedSessionView): Promise<TrainingResult<PlanningView>> {
    const execution = this.executions ? await this.executions.readExecutionForPlannedSession(view.session.sessionId) : { ok: true as const, value: null };
    if (!execution.ok) return execution;
    if (execution.value && (execution.value.context.datasetId !== view.context.datasetId || execution.value.context.controlEpoch !== view.context.controlEpoch)) return { ok: false, code: 'DATASET_STALE' };
    const status = deriveSessionStatus(view.session, execution.value?.execution); if (!status.ok) return status;
    const routine = await this.catalog.readRoutineRevision(view.session.routineRevisionRef); if (!routine.ok) return routine;
    return { ok: true, value: { ...view, routine: routine.value, status: status.value,
      ...(execution.value ? { executionId: execution.value.execution.executionId } : {}) } };
  }
  async getTrainingForDate(date: string): Promise<TrainingResult<PlanningView | null>> {
    const result = await this.repository.readPlannedSessionByDate(date);
    if (!result.ok) return result;
    return result.value === null ? { ok: true, value: null } : this.present(result.value);
  }
  async getPlannedSession(id: string): Promise<TrainingResult<PlanningView>> {
    const result = await this.repository.readPlannedSession(id);
    return result.ok ? this.present(result.value) : result;
  }
  getPlannedSessionRevision(id: string, revision: number) { return this.repository.readPlannedSessionRevision(id, revision); }
  private async saved(id: string, effect: ReprogramSessionEffect): Promise<TrainingResult<PlanningSaved>> {
    const view = await this.getPlannedSession(id);
    return view.ok ? { ok: true, value: { view: view.value, effect } } : view;
  }
  createPlannedSession(routineRevisionRef: string, localDate: string) {
    return intent<PlanSessionCommand, PlanningSaved>(async () => {
      const context = await this.catalog.readContext(); if (!context.ok) return context;
      return { ok: true, value: { operationId: this.id(), context: context.value, session: {
        sessionId: this.id(), routineRevisionRef, cohortLocalDate: localDate,
        plannedLocalDate: localDate, revision: 1, explicitOmission: false,
      } } };
    }, c => this.repository.planSession(c), c => this.saved(c.session.sessionId, 'CHANGED'));
  }
  reprogramPlannedSession(view: PlannedSessionView, targetLocalDate: string) {
    const observed = structuredClone(view);
    let effect: ReprogramSessionEffect = 'CHANGED';
    return intent(async () => ({ ok: true, value: { operationId: this.id(), context: observed.context,
      sessionId: observed.session.sessionId, expectedRevision: observed.session.revision, targetLocalDate } }),
    async c => { const r = await this.repository.reprogramSession(c); if (r.ok) effect = r.value.effect; return r; },
    c => this.saved(c.sessionId, effect));
  }
  omitPlannedSession(view: PlannedSessionView) {
    const observed = structuredClone(view);
    return intent(async () => ({ ok: true, value: { operationId: this.id(), context: observed.context,
      sessionId: observed.session.sessionId, expectedRevision: observed.session.revision } }),
    c => this.repository.omitSession(c), c => this.saved(c.sessionId, 'CHANGED'));
  }
}
